import { ISearchFilterRef, TableProps } from '@/components';
import {
	alert16ReplaceText,
	TableNameMap,
	TableTypes,
	TableValuesMap,
} from '@/constant';
import { OutputType } from '@/hooks';
import {
	TDeleteWorkerAssignmentBody,
	TDeleteWorkerDocumentRequest,
	TDeleteWorkerUnauthorizedDepartureBody,
} from '@/services';
import {
	CommonInquiryBoardActions,
	CommonInquiryBoardAnswerActions,
	CommonNoticeActions,
	CommonNoticeReceiveActions,
	ContractedForeignBusinessActions,
	DistrictAllowIpActions,
	FooterInfoActions,
	ForeignApplicantActions,
	ForeignSystemUnitActions,
	ForeignUserActions,
	SystemUnitActions,
	SystemUserActions,
	WorkerAssignmentActions,
	WorkerAssignmentFranchiseActions,
	WorkerAssignmentPublicActions,
	WorkerChangeWorkPlaceActions,
	WorkerDocumentActions,
	WorkerExtensionStayActions,
	WorkerFranchiseActions,
	WorkerLodgingActions,
	WorkerProgramActions,
	WorkerProgramStatusActions,
	WorkerSalaryActions,
	WorkerUnauthorizedDepartureActions,
	WorkerUserActions,
} from '@/stores';
import { SystemUseTipActions } from '@/stores/system-use-tip';
import { WorkerApplicantActions } from '@/stores/worker-applicant';
import { WorkerRecommenderActions } from '@/stores/worker-recommender';
import {
	getTranslatedMessage,
	insertObjectIfV2,
	NOOP,
	showAlert,
	showToast,
} from '@/utils';
import { isMobile } from '@/utils/mobile.util';
import { RefObject, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { isNotUndefined } from 'ramda-adjunct';

type Params<T extends TableTypes> = {
	tableType?: T;
	rowSelection?: TableProps['rowSelection'] & {
		onReset?: () => void;
	};
	searchFilterRef?: RefObject<ISearchFilterRef>;
	listIds?: number[];
	onDeleteSuccessCallback?: (data: any) => void;
	onDeleteFailedCallback?: (error: any) => void;
};

export const useDeleteListSubmit = <T extends TableTypes>(
	params: Params<T>
) => {
	const {
		tableType,
		rowSelection,
		searchFilterRef,
		listIds,
		onDeleteSuccessCallback,
		onDeleteFailedCallback,
	} = params;

	const { t } = useTranslation(['message', 'validation']);
	const { t: tMenu } = useTranslation('menu');

	const dispatch = useDispatch();

	const selectedRowKeys = useMemo(
		() => (isMobile ? listIds : rowSelection?.selectedRowKeys?.map(Number)),
		[rowSelection?.selectedRowKeys, listIds, isMobile]
	);

	const selectedRows = useMemo(
		() => rowSelection?.selectedRows,
		[rowSelection?.selectedRows]
	);

	const deleteRequest = useCallback(
		<TDelete extends any[] = number[]>(
			callback?: () => void,
			ids?: TDelete
		) => {
			return {
				ids: (ids as TDelete) || (selectedRowKeys as number[]),
				callback: (error: any) => {
					if (error) {
						if (isNotUndefined(onDeleteFailedCallback)) {
							onDeleteFailedCallback?.(error);
						}
						return;
					}
					rowSelection?.onReset?.();

					if (isNotUndefined(onDeleteSuccessCallback)) {
						onDeleteSuccessCallback?.(ids || selectedRowKeys);
					} else {
						searchFilterRef?.current?.onSearchChange?.(1);
					}

					showToast(t('message:DELETE_SUCCESS'));

					callback?.();
				},
			};
		},
		[
			t,
			selectedRowKeys,
			rowSelection?.onReset,
			searchFilterRef?.current,
			onDeleteSuccessCallback,
			onDeleteFailedCallback,
		]
	);

	const onDeleteSubmit = useCallback(
		(callbackSuccess?: () => void) => {
			if (!tableType) return;

			if (selectedRowKeys && !selectedRowKeys.length) {
				showAlert(
					t('validation:ALERT_16', {
						name: alert16ReplaceText(
							getTranslatedMessage(tMenu, TableNameMap[tableType], {
								ns: 'menu',
							}) as string
						),
					})
				);

				return;
			}

			showAlert(
				t('validation:CONFIRM_25'),
				() => {
					switch (tableType) {
						case TableTypes.INQUIRY_BOARD: {
							dispatch(
								CommonInquiryBoardActions.deleteCommonInquiryBoardRequest({
									...deleteRequest(callbackSuccess),
								})
							);
							break;
						}

						case TableTypes.INQUIRY_BOARD_ANSWER: {
							dispatch(
								CommonInquiryBoardAnswerActions.deleteCommonInquiryBoardAnswerRequest(
									{
										...deleteRequest(callbackSuccess),
									}
								)
							);
							break;
						}

						case TableTypes.NOTICE: {
							dispatch(
								CommonNoticeActions.deleteCommonNoticeRequest({
									...deleteRequest(callbackSuccess),
								})
							);
							break;
						}

						case TableTypes.NOTICE_RECEIVE: {
							dispatch(
								CommonNoticeReceiveActions.deleteCommonNoticeReceiveRequest({
									...deleteRequest(callbackSuccess),
								})
							);
							break;
						}

						case TableTypes.UM1_01: {
							dispatch(
								SystemUserActions.deleteSystemUserRequest({
									...deleteRequest(callbackSuccess),
								})
							);
							break;
						}

						case TableTypes.MM1_01: {
							dispatch(
								WorkerUserActions.deleteWorkerUserRequest({
									...deleteRequest(callbackSuccess),
								})
							);
							break;
						}

						case TableTypes.AR1_01: {
							dispatch(
								WorkerApplicantActions.deleteWorkerApplicantRequest({
									...deleteRequest(callbackSuccess),
								})
							);
							break;
						}

						case TableTypes.LM1_01: {
							dispatch(
								WorkerLodgingActions.deleteWorkerLodgingsRequest({
									...deleteRequest(callbackSuccess),
								})
							);
							break;
						}

						case TableTypes.PA4_01: {
							let ids: number[] = [];
							selectedRows?.map((row: OutputType<T>['list'][number]) => {
								const item = row as TableValuesMap[TableTypes.PA4_01];
								if (item) {
									ids = [...ids, item?.applicantSalaryId];
								}
							});
							dispatch(
								WorkerSalaryActions.deleteWorkerSalaryRequest({
									...deleteRequest(callbackSuccess, ids),
								})
							);
							break;
						}

						case TableTypes.BM1_01: {
							let dataDelete: TDeleteWorkerUnauthorizedDepartureBody = [];

							selectedRows?.map((row: OutputType<T>['list'][number]) => {
								const item = row as TableValuesMap[TableTypes.BM1_01];
								dataDelete = [
									...dataDelete,
									{
										id: item.id,
										applicantId: item.applicantId,
										...insertObjectIfV2(item?.unauthorizedDepartureProgramId, {
											programId: item.unauthorizedDepartureProgramId,
										}),
									},
								];
							});

							dispatch(
								WorkerUnauthorizedDepartureActions.deleteWorkerUnauthorizedDepartureRequest(
									{
										...deleteRequest<TDeleteWorkerUnauthorizedDepartureBody>(
											callbackSuccess,
											dataDelete
										),
									}
								)
							);
							break;
						}

						case TableTypes.DM1_01: {
							let dataDelete: TDeleteWorkerDocumentRequest = [];
							const getDocumentMappingIds = (
								data: { fileMapingId: number }[]
							) =>
								(data || []).map(
									(item: { fileMapingId: number }) => item?.fileMapingId
								);
							selectedRows?.map((row: OutputType<T>['list'][number]) => {
								const item = row as TableValuesMap[TableTypes.DM1_01];
								if (item?.fileId) {
									const fileMappingIds = [
										...getDocumentMappingIds(item.fileEmployerName),
										...getDocumentMappingIds(item.fileApplicantFullName),
										...getDocumentMappingIds(item.fileRecommenderName),
										...getDocumentMappingIds(item.fileLodgingAddress),
									];
									dataDelete = [
										...dataDelete,
										{
											fileId: item.fileId,
											fileMappingIds,
										},
									];
								}
							});
							dispatch(
								WorkerDocumentActions.deleteWorkerDocumentRequest({
									...deleteRequest<TDeleteWorkerDocumentRequest>(
										callbackSuccess,
										dataDelete
									),
								})
							);
							break;
						}

						case TableTypes.PM1_01: {
							dispatch(
								WorkerProgramActions.deleteWorkerProgramRequest({
									...deleteRequest(callbackSuccess),
								})
							);
							break;
						}

						case TableTypes.PM2_01: {
							dispatch(
								WorkerProgramStatusActions.deleteWorkerProgramStatusRequest({
									...deleteRequest(callbackSuccess),
								})
							);
							break;
						}

						case TableTypes.AM1_01: {
							dispatch(
								WorkerAssignmentFranchiseActions.deleteWorkerAssignmentFranchiseRequest(
									{
										...deleteRequest(callbackSuccess),
									}
								)
							);
							break;
						}

						case TableTypes.PA1_01: {
							dispatch(
								WorkerAssignmentPublicActions.deleteWorkerAssignmentPublicRequest(
									{
										...deleteRequest(callbackSuccess),
									}
								)
							);
							break;
						}

						case TableTypes.AM3_01: {
							let dataDelete: TDeleteWorkerAssignmentBody = [];

							selectedRows?.map((row: OutputType<T>['list'][number]) => {
								const item = row as TableValuesMap[TableTypes.AM3_01];
								dataDelete = [
									...dataDelete,
									{
										assignmentFranchiseId: item.assignmentFranchiseId,
										...insertObjectIfV2(item?.applicantId, {
											applicantId: item?.applicantId,
										}),
									},
								];
							});

							dispatch(
								WorkerAssignmentActions.deleteWorkerAssignmentRequest({
									...deleteRequest<TDeleteWorkerAssignmentBody>(
										callbackSuccess,
										dataDelete
									),
								})
							);
							break;
						}

						case TableTypes.CM1_01: {
							dispatch(
								SystemUnitActions.deleteSystemUnitRequest({
									...deleteRequest(callbackSuccess),
								})
							);
							break;
						}

						case TableTypes.RM1_01: {
							dispatch(
								WorkerRecommenderActions.deleteWorkerRecommendersRequest({
									...deleteRequest(callbackSuccess),
								})
							);
							break;
						}

						case TableTypes.WC1_01: {
							dispatch(
								WorkerChangeWorkPlaceActions.deleteWorkerChangeWorkPlaceRequest(
									{
										...deleteRequest(callbackSuccess),
									}
								)
							);
							break;
						}

						case TableTypes.EM1_01: {
							let ids: number[] = [];
							selectedRows?.map((row: OutputType<T>['list'][number]) => {
								const item = row as TableValuesMap[TableTypes.EM1_01];
								if (item) {
									ids = [...ids, item?.extensionStayApplicantId];
								}
							});
							dispatch(
								WorkerExtensionStayActions.deleteWorkerExtensionStayRequest({
									...deleteRequest(callbackSuccess, ids),
								})
							);
							break;
						}

						case TableTypes.FR1_01: {
							dispatch(
								WorkerFranchiseActions.deleteWorkerFranchiseRequest({
									...deleteRequest(callbackSuccess),
								})
							);
							break;
						}

						case TableTypes.OI2_01: {
							dispatch(
								ForeignUserActions.deleteForeignUserRequest({
									...deleteRequest(callbackSuccess),
								})
							);
							break;
						}

						case TableTypes.OR1_01: {
							dispatch(
								ForeignApplicantActions.deleteForeignApplicantRequest({
									...deleteRequest(callbackSuccess),
								})
							);
							break;
						}

						case TableTypes.FOOTER_INFO: {
							dispatch(
								FooterInfoActions.deleteFooterInfoRequest({
									...deleteRequest(callbackSuccess),
								})
							);
							break;
						}
						case TableTypes.CONTRACTED_FOREIGN_BUSINESS: {
							dispatch(
								ContractedForeignBusinessActions.deleteContractedForeignBusinessRequest(
									{
										...deleteRequest(callbackSuccess),
									}
								)
							);
							break;
						}
						case TableTypes.CM2_01: {
							dispatch(
								ForeignSystemUnitActions.deleteSystemUnitRequest({
									...deleteRequest(callbackSuccess),
								})
							);
							break;
						}
						case TableTypes.SM1_01: {
							dispatch(
								SystemUseTipActions.deleteSystemUseTipRequest({
									...deleteRequest(callbackSuccess),
								})
							);
							break;
						}
						case TableTypes.DISTRICT_ALLOW_IP: {
							dispatch(
								DistrictAllowIpActions.deleteDistrictAllowIpRequest({
									...deleteRequest(callbackSuccess),
								})
							);
							break;
						}

						default:
							break;
					}
				},
				NOOP
			);
		},
		[t, tMenu, tableType, selectedRowKeys, deleteRequest, selectedRows]
	);

	return {
		onDeleteSubmit,
	};
};
